import React from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Store, X } from 'lucide-react';
import { InlineLoadingState, LoadingSpinner } from '../../../components/LoadingSpinner';
import { AppSelectDropdown } from '../../../components/AppSelectDropdown';
import { SETTINGS_PATH_INTEGRATION_IDS } from '../../../utils/settingsRoutes';
import { instanceIdFromPathname } from '../../../utils/companyLogo';
import { useLocation, Link } from 'react-router-dom';
import { useEscapeClose } from '../../../hooks/useEscapeClose';
import { launchIdsArticleDeeplink as launchIdsAdl, openIdsShopHtml } from '../utils/idsShopLaunch';

type IdsConnection = {
  id: string;
  companyName: string;
  server: 'open' | 'shk' | 'direct';
};

type PreviewItem = { positionId: string; artNo: string; name: string; qty: number };

type Preview = {
  sendable: number;
  skipped: Array<{ reason: string; count: number }>;
  truncatedArtNo: number;
  unknownUnit: number;
  items?: PreviewItem[];
};

const IDS_CART_POST_MESSAGE_SOURCE = 'meisterpfad-ids';

export function IdsCartButton(props: {
  leadRouteId: string;
  offeringId: string;
  documentTypeLabel: string;
  documentNo?: string;
  onImported?: () => void | Promise<void>;
  className?: string;
  /** Dialog sofort öffnen (Link aus dem KI-Chat mit `&ids=open`). */
  autoOpen?: boolean;
  /** Nach dem automatischen Öffnen, z. B. um den Query-Parameter zu entfernen. */
  onAutoOpened?: () => void;
}) {
  const location = useLocation();
  const instancePrefix = React.useMemo(() => {
    const match = location.pathname.match(/^\/i\/[^/]+/);
    return match ? match[0] : `/i/${instanceIdFromPathname(location.pathname)}`;
  }, [location.pathname]);

  const allowed =
    props.documentTypeLabel.trim() === 'Angebot' ||
    props.documentTypeLabel.trim() === 'Kostenvoranschlag' ||
    props.documentTypeLabel.trim() === 'Auftragsbestätigung' ||
    props.documentTypeLabel.trim() === 'Lieferschein';
  const [open, setOpen] = React.useState(false);
  const [connections, setConnections] = React.useState<IdsConnection[]>([]);
  const [connectionId, setConnectionId] = React.useState('');
  const [preview, setPreview] = React.useState<Preview | null>(null);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [positionMenuOpen, setPositionMenuOpen] = React.useState(false);
  const [pickingPositions, setPickingPositions] = React.useState(false);
  const [modeOfShipment, setModeOfShipment] = React.useState<'Lieferung' | 'Abholung'>('Lieferung');
  const [loading, setLoading] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [pollToken, setPollToken] = React.useState<string | null>(null);
  const importedRef = React.useRef(false);

  const finishImport = React.useCallback(() => {
    if (importedRef.current) return;
    importedRef.current = true;
    setPollToken(null);
    setOpen(false);
    void props.onImported?.();
  }, [props]);

  useEscapeClose(() => setOpen(false), open && !busy);

  const { autoOpen, onAutoOpened } = props;
  const onAutoOpenedRef = React.useRef(onAutoOpened);
  onAutoOpenedRef.current = onAutoOpened;
  React.useEffect(() => {
    if (!autoOpen || !allowed) return;
    setOpen(true);
    onAutoOpenedRef.current?.();
  }, [autoOpen, allowed]);

  React.useEffect(() => {
    if (open) return;
    setPositionMenuOpen(false);
    setPickingPositions(false);
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch('/api/integrations/ids/connections', { credentials: 'include' })
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (cancelled) return;
        if (!ok) throw new Error(d?.error || 'Verbindungen konnten nicht geladen werden.');
        const list: IdsConnection[] = Array.isArray(d.connections) ? d.connections : [];
        setConnections(list);
        setConnectionId((prev) => prev || list[0]?.id || '');
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  React.useEffect(() => {
    if (!open || !connectionId || !allowed) return;
    let cancelled = false;
    setPreview(null);
    setSelectedIds([]);
    setPositionMenuOpen(false);
    setPickingPositions(false);
    fetch(`/api/leads/${encodeURIComponent(props.leadRouteId)}/offerings/${encodeURIComponent(props.offeringId)}/ids/preview`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ connectionId }),
    })
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (cancelled) return;
        if (!ok) throw new Error(d?.error || 'Vorschau fehlgeschlagen.');
        const next = d as Preview;
        setPreview(next);
        setSelectedIds((next.items ?? []).map((item) => item.positionId));
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [open, connectionId, allowed, props.leadRouteId, props.offeringId]);

  React.useEffect(() => {
    if (!pollToken) return;
    let cancelled = false;
    const t = window.setInterval(() => {
      fetch(`/api/integrations/ids/sessions/${encodeURIComponent(pollToken)}`, { credentials: 'include' })
        .then((r) => r.json())
        .then((d) => {
          if (cancelled) return;
          if (d?.status === 'applied') {
            window.clearInterval(t);
            finishImport();
          }
        })
        .catch(() => undefined);
    }, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, [pollToken, finishImport]);

  React.useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data as { source?: string; status?: string; offeringId?: string | null } | null;
      if (!data || data.source !== IDS_CART_POST_MESSAGE_SOURCE) return;
      if (data.status !== 'applied') return;
      if (data.offeringId && data.offeringId !== props.offeringId) return;
      finishImport();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [finishImport, props.offeringId]);

  const launch = async (action: 'WKS' | 'WKE') => {
    const popup = window.open('about:blank', '_blank');
    setBusy(true);
    setError(null);
    importedRef.current = false;
    try {
      const res = await fetch(
        `/api/leads/${encodeURIComponent(props.leadRouteId)}/offerings/${encodeURIComponent(props.offeringId)}/ids/launch`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            connectionId,
            action,
            documentTypeLabel: props.documentTypeLabel,
            modeOfShipment,
            documentNo: props.documentNo ?? '',
            ...(action === 'WKS' && pickingPositions ? { positionIds: selectedIds } : {}),
          }),
        },
      );
      const d = await res.json();
      if (!res.ok) throw new Error(d?.error || 'Shop konnte nicht geöffnet werden.');
      if (!openIdsShopHtml(String(d.html || ''), popup)) {
        throw new Error('Popup wurde blockiert. Bitte Popups für Meisterpfad erlauben.');
      }
      if (d.token) setPollToken(String(d.token));
    } catch (err) {
      popup?.close();
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (!allowed) return null;

  return (
    <>
      <button type="button" className={props.className} onClick={() => setOpen(true)}>
        <Store className="w-3.5 h-3.5 shrink-0 text-pipedrive-blue" />
        Im Shop öffnen
      </button>
      {open
        ? createPortal(
            <div className="fixed inset-0 z-[400] flex items-center justify-center p-4 bg-black/30" onClick={() => !busy && setOpen(false)}>
              <div
                className="w-full max-w-xl rounded-lg border border-app-border bg-app-surface shadow-lg"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between px-4 py-3 border-b border-app-border">
                  <h2 className="text-sm font-semibold text-app-text">Großhändler-Shop</h2>
                  <button type="button" className="p-1 rounded-md hover:bg-app-hover" onClick={() => setOpen(false)} aria-label="Schließen">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="p-4 space-y-4 text-sm">
                  {loading ? (
                    <InlineLoadingState label="Verbindungen werden geladen …" />
                  ) : connections.length === 0 ? (
                    <p className="text-app-text-muted">
                      Keine IDS-Verbindung.{' '}
                      <Link className="text-pipedrive-blue hover:underline" to={`${instancePrefix}${SETTINGS_PATH_INTEGRATION_IDS}`}>
                        In den Einstellungen anlegen
                      </Link>
                    </p>
                  ) : (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-app-text-muted mb-1">Großhändler</label>
                        <AppSelectDropdown
                          value={connectionId}
                          onChange={setConnectionId}
                          options={connections.map((c) => ({ value: c.id, label: c.companyName }))}
                          className="w-full"
                          triggerClassName="!min-h-0 h-9 !py-0"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-app-text-muted mb-1">Versand</label>
                        <AppSelectDropdown
                          value={modeOfShipment}
                          onChange={(v) => setModeOfShipment(v === 'Abholung' ? 'Abholung' : 'Lieferung')}
                          options={[
                            { value: 'Lieferung', label: 'Lieferung' },
                            { value: 'Abholung', label: 'Abholung' },
                          ]}
                          className="w-full"
                          triggerClassName="!min-h-0 h-9 !py-0"
                        />
                      </div>
                      {error ? <p className="text-sm text-red-700">{error}</p> : null}
                      <div className="flex flex-col gap-2">
                        {!preview ? (
                          <InlineLoadingState label="Positionen werden geprüft …" className="justify-start" />
                        ) : preview.skipped.length || preview.truncatedArtNo || preview.unknownUnit ? (
                          <p className="text-xs text-app-text-subtle">
                            {preview.sendable} Positionen
                            {preview.skipped.length
                              ? `, ${preview.skipped.map((s) => `${s.count} ${s.reason}`).join(', ')}.`
                              : '.'}
                            {preview.truncatedArtNo ? ` ${preview.truncatedArtNo} Artikelnummern gekürzt.` : ''}
                            {preview.unknownUnit ? ` ${preview.unknownUnit} unbekannte Einheiten als PCE.` : ''}
                          </p>
                        ) : null}
                        {pickingPositions && (preview?.items?.length ?? 0) > 0 ? (
                          <>
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-xs text-app-text-subtle">
                                {selectedIds.length} von {preview?.items?.length ?? 0} Positionen ausgewählt
                              </p>
                              <button
                                type="button"
                                className="text-xs font-medium text-pipedrive-blue hover:underline"
                                onClick={() => {
                                  const all = (preview?.items ?? []).map((item) => item.positionId);
                                  setSelectedIds((prev) => (prev.length === all.length ? [] : all));
                                }}
                              >
                                {selectedIds.length === (preview?.items?.length ?? 0) ? 'Keine' : 'Alle'}
                              </button>
                            </div>
                            <div className="max-h-48 overflow-auto rounded-md border border-app-border">
                              <table className="w-full border-collapse text-xs text-app-text">
                                <thead>
                                  <tr className="bg-app-surface-muted border-b border-app-border">
                                    <th className="w-8 px-2 py-1.5" />
                                    <th className="text-left font-semibold px-2 py-1.5">Artikel</th>
                                    <th className="text-right font-semibold px-2 py-1.5 w-16">Menge</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(preview?.items ?? []).map((item) => (
                                    <tr key={item.positionId} className="border-b border-app-border last:border-0">
                                      <td className="px-2 py-1.5 text-center">
                                        <input
                                          type="checkbox"
                                          checked={selectedIds.includes(item.positionId)}
                                          onChange={() =>
                                            setSelectedIds((prev) =>
                                              prev.includes(item.positionId)
                                                ? prev.filter((id) => id !== item.positionId)
                                                : [...prev, item.positionId],
                                            )
                                          }
                                          aria-label={`${item.name || item.artNo} auswählen`}
                                        />
                                      </td>
                                      <td className="px-2 py-1.5">
                                        <div className="truncate">{item.name || item.artNo}</div>
                                        <div className="text-app-text-subtle">{item.artNo}</div>
                                      </td>
                                      <td className="px-2 py-1.5 text-right tabular-nums">{item.qty}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </>
                        ) : null}
                        <div className="relative">
                          <div className="flex">
                            <button
                              type="button"
                              disabled={
                                busy ||
                                !preview ||
                                (pickingPositions ? selectedIds.length === 0 : (preview.items?.length ?? preview.sendable) === 0)
                              }
                              onClick={() => {
                                setPositionMenuOpen(false);
                                void launch('WKS');
                              }}
                              className={`inline-flex h-9 flex-1 items-center justify-center gap-2 bg-pipedrive-blue px-3.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-40 ${
                                (preview?.items?.length ?? 0) > 0 ? 'rounded-l-md' : 'rounded-md'
                              }`}
                            >
                              {busy ? <LoadingSpinner size="sm" /> : null}
                              Warenkorb im Shop öffnen
                            </button>
                            {(preview?.items?.length ?? 0) > 0 ? (
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => setPositionMenuOpen((openMenu) => !openMenu)}
                                aria-haspopup="menu"
                                aria-expanded={positionMenuOpen}
                                aria-label="Positionen auswählen"
                                title="Positionen auswählen"
                                className="inline-flex h-9 items-center rounded-r-md border-l border-blue-400/60 bg-pipedrive-blue px-2 text-white hover:bg-blue-700 disabled:opacity-40"
                              >
                                <ChevronDown
                                  className={`h-4 w-4 transition-transform ${positionMenuOpen || pickingPositions ? 'rotate-180' : ''}`}
                                />
                              </button>
                            ) : null}
                          </div>
                          {positionMenuOpen ? (
                            <div
                              role="menu"
                              className="absolute right-0 top-full z-10 mt-1 w-72 overflow-hidden rounded-md border border-app-border bg-app-surface shadow-lg"
                            >
                              <button
                                type="button"
                                role="menuitem"
                                onClick={() => {
                                  const all = (preview?.items ?? []).map((item) => item.positionId);
                                  setSelectedIds(all);
                                  setPickingPositions((prev) => !prev);
                                  setPositionMenuOpen(false);
                                }}
                                className="block w-full px-3 py-2 text-left hover:bg-app-hover"
                              >
                                <span className="block text-sm font-medium text-app-text">
                                  {pickingPositions ? 'Alle Positionen verwenden' : 'Positionen auswählen'}
                                </span>
                                <span className="block text-xs text-app-text-muted">
                                  {pickingPositions
                                    ? 'Wieder alle passenden Artikel in den Warenkorb legen'
                                    : 'Nur bestimmte Artikel in den Warenkorb legen'}
                                </span>
                              </button>
                            </div>
                          ) : null}
                        </div>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void launch('WKE')}
                          className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-app-border bg-app-surface px-3.5 text-sm font-medium text-app-text-muted hover:bg-app-hover disabled:opacity-40"
                        >
                          Im Shop zusammenstellen
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

export async function launchIdsArticleDeeplink(opts: {
  leadRouteId: string;
  offeringId: string;
  connectionId?: string;
  articleNumber: string;
}): Promise<void> {
  return launchIdsAdl(opts);
}
